"use client";

import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  Bus,
  Car,
  HelpCircle,
  ImageIcon,
  MoreVerticalIcon,
  Truck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CreateVehicle from "./Create";
import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import VehicleImages from "./VehicleImages";
import { Vehicle } from "@/interface/entityInterface";
import { tableCol } from "@/components/common/columns/columns";
import VehicleEdit from "./VehicleEdit";
import CommonDelete from "@/components/common/Delete";
import VehicleDetails from "./VehicleDetails";
import CarLogo from "@/components/CarLogo";

export type Order = {
  id: number;
  client_name: string;
  client_phone: string;
  pickup: string;
  dropoff: string;
  status:
    | "NEW_ORDER"
    | "IN_PROGRESS"
    | "ASSIGNED"
    | "ACCEPTED"
    | "REJECTED"
    | "CANCELLED"
    | "COMPLETED";
  created_at: string;
};

const typeIcons: Record<string, JSX.Element> = {
  truck: <Truck className="w-4 h-4 mr-1 text-primary" />,
  car: <Car className="w-4 h-4 mr-1 text-primary" />,
  bus: <Bus className="w-4 h-4 mr-1 text-primary" />,
  motorcycle:  <Bus className="w-4 h-4 mr-1 text-primary" />,
};

export const VehiclesList = () => {
  // Add state for the VehicleImages sheet
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [showImagesSheet, setShowImagesSheet] = useState(false);
  const [vehicleDetails, setDetails] = useState<Vehicle | null>(null);
  const [isDetailOpen, setDetailOpen] = useState(false);

  const rowClick = (e: any, vehicle: Vehicle) => {
    setDetailOpen(true)
    setDetails(vehicle)
  }

  // Update the showImages function
  const handleShowImages = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowImagesSheet(true);
  };

  const columns: ColumnDef<Vehicle>[] = tableCol({
    showActions: false,
    columns: [
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => {
          const type: string = row.original.type;
          const icon = typeIcons[type] || (
            <HelpCircle className="w-4 h-4 mr-1 text-muted-foreground" />
          );

          return (
            <div className="flex items-center text-sm capitalize">{icon}</div>
          );
        },
      },
      {
        accessorKey: "brand_model",
        header: "Brand & Model",
        cell: ({ row }) => {
          console.log(row.original)
          return (
            <div className="flex flex-row items-center">
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-[#ddd] mr-3">
                <CarLogo 
                  brand={row.original.make.name}
                  fallback={row.original.make.logo}
                  size={32}
                />
              </div>
              <div className="flex flex-col text-sm space-y-1">
                <span className="font-medium">{row.original.make.name}</span>
                <span className="text-[10px]">
                  {row.original.make_model
                    ? `${row.original.make_model} (${row.original.make_year})`
                    : `(${row.original.make_year})`}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "nickname",
        header: "Identity",
        cell: ({ row }) => (
          <div className="flex flex-col text-sm space-y-1">
            <div className="text-sm">{row.original.license_plate || "-"}</div>
            <div className="text-[10px]">{row.original.nickname || "-"}</div>
          </div>
        ),
      },
      {
        accessorKey: "active",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={`text-xs font-semibold px-2 py-1 rounded-full ${
              row.original.active
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {row.original.active ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <VehicleEdit prop={row.original.id} />
              <DropdownMenuItem onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                handleShowImages(row.original)
              }}>
                <ImageIcon className="w-4 h-4" />
                View Images
              </DropdownMenuItem>
              <CommonDelete id={row.original.id} resource="vehicles" />
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
  });

  return (
    <>
      <CommonList>
        <CommonTableList
          columns={columns}
          filterSearch="nickname"
          create={<CreateVehicle />}
          rowClick={rowClick}
        />
      </CommonList>

      {showImagesSheet && <VehicleImages
        isOpen={showImagesSheet}
        onClose={() => setShowImagesSheet(false)}
        vehicle={selectedVehicle}
      />}
      <VehicleDetails isSheetOpen={isDetailOpen} setIsSheetOpen={setDetailOpen} data={vehicleDetails} />
    </>
  );
};

export default VehiclesList;
